// User Auth Context - Supports Google OAuth AND Email/Password
import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const UserAuthContext = createContext();

// Configure axios to send cookies
axios.defaults.withCredentials = true;

// Helper to format API error messages
function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export const UserAuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Check if user is authenticated on mount
  const checkAuth = useCallback(async () => {
    // Skip if returning from Google OAuth callback
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    // Try Google auth first
    try {
      const googleResponse = await axios.get(`${API_URL}/api/auth/google/me`, {
        withCredentials: true
      });
      
      if (googleResponse.data.success && googleResponse.data.user) {
        setUser({ ...googleResponse.data.user, auth_provider: 'google' });
        setLoading(false);
        return;
      }
    } catch (error) {
      // Google auth failed, try email/password auth
    }

    // Try email/password auth
    try {
      const emailResponse = await axios.get(`${API_URL}/api/auth/customer/me`, {
        withCredentials: true
      });
      
      if (emailResponse.data.success && emailResponse.data.user) {
        setUser(emailResponse.data.user);
        setLoading(false);
        return;
      }
    } catch (error) {
      // Not authenticated
    }

    setUser(false);
    setLoading(false);
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // ========== Google OAuth ==========
  
  const loginWithGoogle = () => {
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const exchangeGoogleSession = async (sessionId) => {
    try {
      const response = await axios.post(
        `${API_URL}/api/auth/google/session`,
        { session_id: sessionId },
        { withCredentials: true }
      );
      
      if (response.data.success && response.data.user) {
        setUser({ ...response.data.user, auth_provider: 'google' });
        return { success: true, user: response.data.user };
      }
      return { success: false, error: 'Authentication failed' };
    } catch (error) {
      return { 
        success: false, 
        error: formatApiErrorDetail(error.response?.data?.detail)
      };
    }
  };

  // ========== Email/Password Auth ==========

  const register = async (email, password, name) => {
    try {
      const response = await axios.post(
        `${API_URL}/api/auth/customer/register`,
        { email, password, name },
        { withCredentials: true }
      );
      
      if (response.data.success && response.data.user) {
        setUser(response.data.user);
        return { success: true, user: response.data.user };
      }
      return { success: false, error: 'Registration failed' };
    } catch (error) {
      return { 
        success: false, 
        error: formatApiErrorDetail(error.response?.data?.detail)
      };
    }
  };

  const login = async (email, password) => {
    try {
      const response = await axios.post(
        `${API_URL}/api/auth/customer/login`,
        { email, password },
        { withCredentials: true }
      );
      
      if (response.data.success && response.data.user) {
        setUser(response.data.user);
        return { success: true, user: response.data.user };
      }
      return { success: false, error: 'Login failed' };
    } catch (error) {
      return { 
        success: false, 
        error: formatApiErrorDetail(error.response?.data?.detail)
      };
    }
  };

  const forgotPassword = async (email) => {
    try {
      const response = await axios.post(
        `${API_URL}/api/auth/customer/forgot-password`,
        { email },
        { withCredentials: true }
      );
      return { success: true, message: response.data.message };
    } catch (error) {
      return { 
        success: false, 
        error: formatApiErrorDetail(error.response?.data?.detail)
      };
    }
  };

  const resetPassword = async (token, newPassword) => {
    try {
      const response = await axios.post(
        `${API_URL}/api/auth/customer/reset-password`,
        { token, new_password: newPassword },
        { withCredentials: true }
      );
      return { success: true, message: response.data.message };
    } catch (error) {
      return { 
        success: false, 
        error: formatApiErrorDetail(error.response?.data?.detail)
      };
    }
  };

  // ========== Logout ==========

  const logout = async () => {
    try {
      // Logout from both providers
      await Promise.allSettled([
        axios.post(`${API_URL}/api/auth/google/logout`, {}, { withCredentials: true }),
        axios.post(`${API_URL}/api/auth/customer/logout`, {}, { withCredentials: true })
      ]);
    } catch (error) {
      // Ignore logout errors
    }
    setUser(false);
  };

  return (
    <UserAuthContext.Provider value={{ 
      user, 
      loading, 
      // Google OAuth
      loginWithGoogle,
      exchangeGoogleSession,
      // Email/Password
      register,
      login,
      forgotPassword,
      resetPassword,
      // Common
      logout, 
      checkAuth,
      isAuthenticated: !!user 
    }}>
      {children}
    </UserAuthContext.Provider>
  );
};

export const useUserAuth = () => {
  const context = useContext(UserAuthContext);
  if (!context) {
    throw new Error('useUserAuth must be used within a UserAuthProvider');
  }
  return context;
};

export default UserAuthContext;
